import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { ResponseOrganization } from "@dashboard-v3/api";
import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Typography,
} from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import apiRequest from "utils/apiRequestWithErrorCode";
import { isBlank } from "utils/string";
import { SecretTransitionModal } from "./SecretTransitionModal";
import { RemoveSecretModal } from "./RemoveSecretModal";
import { FieldWithButtonContainer } from "../common/styled";
import FieldActions from "../common/FieldActions";

export interface Props {
  gateway: ResponseOrganization["gateway"];
  onNewSecret: (organization: ResponseOrganization) => void;
}

type State = {
  isTransitionModalOpen: boolean;
  isRemoveModalOpen: boolean;
  isSaving: boolean;
  isRemoving: boolean;
};

function initState() {
  return {
    isTransitionModalOpen: false,
    isRemoveModalOpen: false,
    isSaving: false,
    isRemoving: false,
  };
}

export function GatewaySecret(props: Props) {
  const { t } = useTranslation("gateway");
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<State>(initState);
  const { secret } = props.gateway;
  const secretExists = !isBlank(secret.currentSecret);
  const secretExpiration = dayjs(secret.changedAt)
    .add(secret.minutesWhilePreviousIsValid, "minute")
    .format("hh:mm A");

  function updateState(update: Partial<State>) {
    setState(prev => ({ ...prev, ...update }));
  }

  async function saveSecret(transition?: number) {
    updateState({ isSaving: true });
    try {
      const path = "/organization/gateway-secret";
      const body = {
        ...(Boolean(transition) && { minutesWhilePreviousIsValid: transition }),
      };
      const response = await apiRequest<ResponseOrganization>(
        "POST",
        path,
        body
      );
      props.onNewSecret(response);
    } catch (e) {
      enqueueSnackbar(t(`gatewaySecret.generate.error`), { variant: "error" });
    } finally {
      updateState({ isSaving: false });
    }
  }

  async function generateNewSecret() {
    if (!isBlank(secret.currentSecret)) {
      return updateState({ isTransitionModalOpen: true });
    }

    saveSecret();
  }

  async function refreshSecret(transition?: number) {
    await saveSecret(transition);
    updateState({ isTransitionModalOpen: false });
  }

  async function removeSecretConfig() {
    updateState({ isRemoving: true });
    try {
      const path = "/organization/gateway-secret";
      const response = await apiRequest<ResponseOrganization>("DELETE", path);
      props.onNewSecret(response);
    } catch (e) {
      enqueueSnackbar(t(`gatewaySecret.delete.error`), { variant: "error" });
    } finally {
      updateState({ isRemoving: false, isRemoveModalOpen: false });
    }
  }

  return (
    <>
      <Box style={{ marginBottom: 20, maxWidth: "60%" }}>
        <Typography variant="subtitle2">{t("gatewaySecret.title")}</Typography>
        <Typography variant="caption" color="textSecondary">
          {t("gatewaySecret.subtitle")}
        </Typography>
      </Box>

      {!secretExists ? (
        <ButtonWithLoading
          variant="contained"
          color="default"
          size="small"
          loading={state.isSaving}
          disableElevation
          onClick={generateNewSecret}
        >
          {t("gatewaySecret.actions.generateNew")}
        </ButtonWithLoading>
      ) : (
        <FieldWithButtonContainer>
          <TextField
            variant="outlined"
            fullWidth
            name="gatewaySecret"
            label={t("gatewaySecret.current.label")}
            value={secret.currentSecret}
            InputProps={{
              disabled: true,
              endAdornment: (
                <FieldActions
                  onGenerate={() => generateNewSecret()}
                  value={secret.currentSecret}
                  generateTooltip={t("gatewaySecret.actions.generateNew")}
                  copyTooltip={t("gatewaySecret.actions.copyCurrent")}
                />
              ),
            }}
          />
        </FieldWithButtonContainer>
      )}
      {showPreviousSecret(secret) && (
        <FieldWithButtonContainer>
          <TextField
            variant="outlined"
            fullWidth
            name="previousGatewaySecret"
            label={t("gatewaySecret.previous.label")}
            value={secret.previousSecret}
            InputProps={{
              disabled: true,
              endAdornment: (
                <FieldActions
                  value={secret.previousSecret}
                  copyTooltip={t("gatewaySecret.actions.copyPrevious")}
                />
              ),
            }}
          />
          <FormHelperText style={{ color: "#009ed2" }}>
            {t("gatewaySecret.expiration.label", { secretExpiration })}
          </FormHelperText>
        </FieldWithButtonContainer>
      )}
      {secretExists && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          disableElevation
          onClick={() => updateState({ isRemoveModalOpen: true })}
        >
          {t("gatewaySecret.removeModal.removeBtn")}
        </Button>
      )}
      {state.isTransitionModalOpen && (
        <SecretTransitionModal
          isOpen={state.isTransitionModalOpen}
          isSaving={state.isSaving}
          onClose={() => updateState({ isTransitionModalOpen: false })}
          onSubmit={minutes => refreshSecret(minutes)}
        />
      )}
      {state.isRemoveModalOpen && (
        <RemoveSecretModal
          isOpen={state.isRemoveModalOpen}
          loading={state.isRemoving}
          onClose={() => updateState({ isRemoveModalOpen: false })}
          onRemove={removeSecretConfig}
        />
      )}
    </>
  );
}

function showPreviousSecret(secret: ResponseOrganization["gateway"]["secret"]) {
  const { changedAt, previousSecret, minutesWhilePreviousIsValid } = secret;

  if (!previousSecret) return false;
  if (!minutesWhilePreviousIsValid) return false;

  return dayjs().isBefore(
    dayjs(changedAt).add(minutesWhilePreviousIsValid, "minute")
  );
}
